import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  deleteAdminImprovementSuggestion,
  fetchAdminImprovementSuggestionImage,
  getAdminImprovementSuggestions,
  getMyProfile,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { formatDateTime } from '../../../app/date';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import { buildImprovementSuggestionActionPrompt } from '../private/buildImprovementSuggestionPrompt';

const truncateText = (text, maxLen = 120) => {
  if (!text) return '';
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
};

const openImageInNewTab = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const SuggestionImageZoomDialog = ({ open, url, title, onClose, translate }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    fullWidth
    PaperProps={{
      sx: {
        width: '98vw',
        maxWidth: '98vw',
        height: '98vh',
        maxHeight: '98vh',
        m: 1,
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 1,
      }}
    >
      <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => openImageInNewTab(url)}
        >
          {translate('improvement_suggestion.open_in_new_tab')}
        </Button>
        <IconButton aria-label={translate('common.close')} onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent
      dividers
      sx={{
        p: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'grey.100',
      }}
    >
      <Box
        component="img"
        src={url}
        alt={title}
        sx={{
          display: 'block',
          width: 'auto',
          height: 'auto',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
      />
    </DialogContent>
  </Dialog>
);

const SuggestionImageBlock = ({ label, url, onZoom, translate }) => (
  <Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 0.5,
      }}
    >
      <Typography variant="subtitle2">{label}</Typography>
      <Button
        size="small"
        startIcon={<OpenInNewIcon fontSize="small" />}
        onClick={() => openImageInNewTab(url)}
      >
        {translate('improvement_suggestion.open_in_new_tab')}
      </Button>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {translate('improvement_suggestion.click_to_enlarge')}
    </Typography>
    <Box
      component="button"
      type="button"
      onClick={() => onZoom(url, label)}
      aria-label={translate('improvement_suggestion.click_to_enlarge')}
      sx={{
        p: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        cursor: 'zoom-in',
        width: '100%',
        display: 'block',
        overflow: 'hidden',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        component="img"
        src={url}
        alt={label}
        sx={{
          width: '100%',
          maxHeight: '50vh',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </Box>
  </Box>
);

const SuggestionDetailDialog = ({ open, item, onClose, onCopyPrompt, onDelete }) => {
  const { translate } = useTranslation();
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageError, setImageError] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [extraUrl, setExtraUrl] = useState('');
  const [zoomImage, setZoomImage] = useState({ open: false, url: '', title: '' });

  useEffect(() => {
    if (!open || !item?.id) {
      return undefined;
    }

    let cancelled = false;
    let shotObjectUrl = '';
    let extraObjectUrl = '';

    const load = async () => {
      setLoadingImages(true);
      setImageError('');
      setScreenshotUrl('');
      setExtraUrl('');

      const errors = [];

      try {
        shotObjectUrl = await fetchAdminImprovementSuggestionImage(item.id, 'screenshot');
        if (cancelled) return;
        setScreenshotUrl(shotObjectUrl);
      } catch (error) {
        errors.push(error.message || translate('improvement_suggestion.screenshot_label'));
      }

      try {
        extraObjectUrl = await fetchAdminImprovementSuggestionImage(item.id, 'extra');
        if (cancelled) return;
        setExtraUrl(extraObjectUrl);
      } catch (_) {
        /* extra image is optional */
      }

      if (!cancelled) {
        if (errors.length > 0 && !shotObjectUrl && !extraObjectUrl) {
          setImageError(errors[0] || translate('improvement_suggestion.admin_images_error'));
        }
        setLoadingImages(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (shotObjectUrl) URL.revokeObjectURL(shotObjectUrl);
      if (extraObjectUrl) URL.revokeObjectURL(extraObjectUrl);
    };
  }, [open, item?.id, translate]);

  if (!item) return null;

  const handleZoomClose = () => {
    setZoomImage({ open: false, url: '', title: '' });
  };

  const handleDetailClose = () => {
    handleZoomClose();
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleDetailClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate('improvement_suggestion.admin_detail_title')}</DialogTitle>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
          <Typography variant="body2">
            <strong>{translate('improvement_suggestion.admin_col_date')}:</strong>{' '}
            {formatDateTime(item.created_at) || item.created_at}
          </Typography>
          <Typography variant="body2">
            <strong>{translate('improvement_suggestion.admin_col_user')}:</strong> {item.user_email}
          </Typography>
          <Typography variant="body2">
            <strong>{translate('improvement_suggestion.admin_col_page')}:</strong>{' '}
            {item.page_title || item.page_path}
          </Typography>
          {item.page_title && (
            <Typography variant="caption" color="text.secondary" display="block">
              {item.page_path}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          {translate('improvement_suggestion.admin_col_description')}
        </Typography>
        <Box
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            mb: 2,
            maxHeight: '40vh',
            overflow: 'auto',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="body2">{item.description}</Typography>
        </Box>

        {loadingImages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {imageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {imageError}
          </Alert>
        )}

        {!loadingImages && screenshotUrl && (
          <Box sx={{ mb: extraUrl ? 2 : 0 }}>
            <SuggestionImageBlock
              label={translate('improvement_suggestion.screenshot_label')}
              url={screenshotUrl}
              onZoom={(url, title) => setZoomImage({ open: true, url, title })}
              translate={translate}
            />
          </Box>
        )}

        {!loadingImages && extraUrl && (
          <SuggestionImageBlock
            label={translate('improvement_suggestion.extra_image_label')}
            url={extraUrl}
            onZoom={(url, title) => setZoomImage({ open: true, url, title })}
            translate={translate}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(item)}
        >
          {translate('common.delete')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => onCopyPrompt(item)}
          >
            {translate('improvement_suggestion.copy_prompt')}
          </Button>
          <Button onClick={handleDetailClose}>{translate('common.close')}</Button>
        </Box>
      </DialogActions>
    </Dialog>

    <SuggestionImageZoomDialog
      open={zoomImage.open}
      url={zoomImage.url}
      title={zoomImage.title}
      onClose={handleZoomClose}
      translate={translate}
    />
    </>
  );
};

const SuggestionThumbnail = ({ item, onView }) => {
  const { translate } = useTranslation();
  const [screenshotUrl, setScreenshotUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const shot = await fetchAdminImprovementSuggestionImage(item.id, 'screenshot');
        if (cancelled) {
          URL.revokeObjectURL(shot);
          return;
        }
        setScreenshotUrl(shot);
      } catch (_) {
        /* ignore thumbnail errors */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  useEffect(() => () => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
  }, [screenshotUrl]);

  if (!screenshotUrl) return null;

  return (
    <Tooltip title={translate('improvement_suggestion.admin_view_details')}>
      <Box
        component="button"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onView(item);
        }}
        sx={{
          p: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
          cursor: 'pointer',
          bgcolor: 'background.paper',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <Box
          component="img"
          src={screenshotUrl}
          alt={translate('improvement_suggestion.screenshot_label')}
          sx={{ width: 72, height: 48, objectFit: 'cover', display: 'block' }}
        />
      </Box>
    </Tooltip>
  );
};

const ImprovementSuggestionsPage = () => {
  const { translate } = useTranslation();
  const [profile, setProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, description: '' });

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminImprovementSuggestions();
      setItems(res.data?.items || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const me = await getMyProfile();
        if (cancelled) return;
        const p = me.data?.profile;
        setProfile(p || '');
        if (p !== 'ADMIN') {
          setLoading(false);
          return;
        }
        await loadItems();
      } catch (error) {
        if (!cancelled) {
          setSnackbar({ open: true, message: error.message, severity: 'error' });
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadItems]);

  const handleView = (item) => {
    setDetailItem(item);
  };

  const handleDetailClose = () => {
    setDetailItem(null);
  };

  const handleDeleteClick = (item, event) => {
    event?.stopPropagation();
    setConfirmDialog({
      open: true,
      id: item.id,
      description: truncateText(item.description, 80),
    });
  };

  const handleDeleteCancel = () => {
    setConfirmDialog({ open: false, id: null, description: '' });
  };

  const handleDeleteConfirm = async () => {
    const { id } = confirmDialog;
    if (!id) return;
    try {
      await deleteAdminImprovementSuggestion(id);
      if (detailItem?.id === id) {
        setDetailItem(null);
      }
      setSnackbar({
        open: true,
        message: translate('improvement_suggestion.admin_deleted'),
        severity: 'success',
      });
      handleDeleteCancel();
      await loadItems();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleCopyPrompt = useCallback(async (item) => {
    if (!item) return;
    try {
      const text = buildImprovementSuggestionActionPrompt(item);
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: translate('improvement_suggestion.copy_prompt_success'),
        severity: 'success',
      });
    } catch (_) {
      setSnackbar({
        open: true,
        message: translate('improvement_suggestion.copy_prompt_error'),
        severity: 'error',
      });
    }
  }, [translate]);

  if (!loading && profile && profile !== 'ADMIN') {
    return <Navigate to="/settings" replace />;
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{translate('improvement_suggestion.admin_col_date')}</TableCell>
              <TableCell>{translate('improvement_suggestion.admin_col_user')}</TableCell>
              <TableCell>{translate('improvement_suggestion.admin_col_page')}</TableCell>
              <TableCell>{translate('improvement_suggestion.admin_col_description')}</TableCell>
              <TableCell>{translate('improvement_suggestion.admin_col_images')}</TableCell>
              <TableCell align="right">{translate('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {translate('improvement_suggestion.admin_empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={() => handleView(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(item.created_at) || item.created_at}
                  </TableCell>
                  <TableCell>{item.user_email}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.page_title || item.page_path}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.page_path}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxWidth: 360,
                    }}
                  >
                    {item.description}
                  </TableCell>
                  <TableCell>
                    <SuggestionThumbnail item={item} onView={handleView} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={translate('improvement_suggestion.copy_prompt_tooltip')}>
                      <IconButton
                        size="small"
                        aria-label={translate('improvement_suggestion.copy_prompt_tooltip')}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCopyPrompt(item);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('improvement_suggestion.admin_view_details')}>
                      <IconButton
                        size="small"
                        aria-label={translate('improvement_suggestion.admin_view_details')}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleView(item);
                        }}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('common.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={translate('common.delete')}
                        onClick={(event) => handleDeleteClick(item, event)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SuggestionDetailDialog
        open={Boolean(detailItem)}
        item={detailItem}
        onClose={handleDetailClose}
        onCopyPrompt={handleCopyPrompt}
        onDelete={handleDeleteClick}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={translate('improvement_suggestion.admin_delete_title')}
        message={translate('improvement_suggestion.admin_delete_message')}
        detail={confirmDialog.description}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText={translate('common.delete')}
        cancelText={translate('common.cancel')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImprovementSuggestionsPage;
